import React, { createContext, useState, useContext, useEffect, ReactNode } from "react";
import { Photo } from "../types/photo";
import { getUserAlbum, getPhotoUrl } from "../services/photoService"

interface AlbumContextType {
    photos: Photo[];
    currentPage: number;
    pageSize: number;
    totalCount: number;
    setCurrentPage: (page: number) => void;
    setPageSize: (size: number) => void;
    setPhotos: (photos: Photo[]) => void;
    setTotalCount: (count: number) => void;
    addPhoto: (newPhoto: Photo) => void;
    removePhoto: (photoId: number) => void;
    updatePhoto: (updatedPhoto: Photo) => void;
}
const AlbumContext = createContext<AlbumContextType | undefined>(undefined);

export const AlbumProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [photos, setPhotosState] = useState<Photo[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(8);

    const setPhotos = (photos: Photo[]) => {
        setPhotosState(photos);
    };

    const addPhoto = (newPhoto: Photo) => {
        if (currentPage === 1) {
            setPhotosState(prev => [newPhoto, ...prev.slice(0, pageSize - 1)]);
        }
        setTotalCount(prev => prev + 1);
    };

    const removePhoto = async (photoId: number) => {
        try {
            // Update total count first (minus 1)
            setTotalCount(prev => Math.max(0, prev - 1));

            // Calculate how many photos to load to refill current page
            const totalNeeded = currentPage * pageSize;

            // Fetch photos from start to cover current page completely
            const { photos: updatedPhotos } = await getUserAlbum(1, totalNeeded);

            // Set correct current page slice
            const currentPagePhotos = updatedPhotos.slice((currentPage - 1) * pageSize, currentPage * pageSize);

            const photosWithUrls = await Promise.all(
                currentPagePhotos.map(async (photo) => ({
                    ...photo,
                    src: await getPhotoUrl(photo.photoId),
                }))
            );
            setPhotosState(photosWithUrls);
        } catch (error) {
            console.error("Failed to update album after deletion:", error);
        }
    };

    const updatePhoto = (updatedPhoto: Photo) => {
        setPhotosState(prev =>
            prev.map(photo => (photo.photoId === updatedPhoto.photoId ? updatedPhoto : photo))
        );
    };

    return (
        <AlbumContext.Provider
            value={{
                photos,
                currentPage,
                pageSize,
                totalCount,
                setCurrentPage,
                setPageSize,
                setPhotos,
                setTotalCount,
                addPhoto,
                removePhoto,
                updatePhoto,
            }}
        >
            {children}
        </AlbumContext.Provider>
    );
};

export const useAlbum = () => {
    const context = useContext(AlbumContext);
    if (!context) {
        throw new Error("useAlbum must be used within an AlbumProvider");
    }
    return context;
};